import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { X, Gift, CheckCircle2, Download } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { useLang } from "../../lib/LanguageContext";
import { trackEvent } from "../../lib/analytics";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STORAGE_KEY = "asc-exit-shown";
const AUTO_OPEN_MS = 4000; // show after 4s on every device, regardless of intent

const COPY = {
  id: {
    badge: "🎁 GRATIS — LIMITED",
    title: "Tunggu! Ambil Katalog Top 50 Produk Grosir Margin Tinggi",
    body: "50 produk non-marketplace dengan margin 150%+ yang sedang trending di Indonesia. Update terbaru setiap minggu. Kami kirim akses katalognya ke email Anda.",
    emailLabel: "Email Anda",
    submit: "Kirim Akses Katalog Gratis",
    submitting: "Mengirim…",
    successTitle: "Akses katalog sudah meluncur ke email Anda!",
    successBody: "Cek inbox utama (atau folder Promosi/Spam) dalam 1–2 menit. Sambil menunggu, yuk daftar akun gratis.",
    successCta: "Daftar Akun Gratis",
    noThanks: "Tidak, terima kasih",
    errorMsg: "Gagal mengirim. Coba lagi ya.",
    feature1: "50 produk terkurasi",
    feature2: "Margin 150%+",
    feature3: "Update tiap minggu",
  },
  en: {
    badge: "🎁 FREE — LIMITED",
    title: "Wait! Grab the Top 50 High-Margin Grosir Catalog",
    body: "50 non-marketplace products with 150%+ margin trending in Indonesia right now. Updated every week. We'll email you the catalog access.",
    emailLabel: "Your email",
    submit: "Send My Free Catalog",
    submitting: "Sending…",
    successTitle: "Your catalog is on its way!",
    successBody: "Check your inbox (or Promotions/Spam) in 1–2 minutes. While you wait, create your free account.",
    successCta: "Create Free Account",
    noThanks: "No thanks",
    errorMsg: "Something went wrong. Please try again.",
    feature1: "50 curated products",
    feature2: "150%+ margins",
    feature3: "Weekly updates",
  },
};

const REGISTER_URL = "https://app.asiacommerce.net/register";

export default function ExitIntentPopup() {
  const { lang } = useLang();
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");
  const c = COPY[lang] || COPY.id;

  const shouldSkip = useCallback(() => {
    if (typeof window === "undefined") return true;
    return window.sessionStorage.getItem(STORAGE_KEY) === "1";
  }, []);

  useEffect(() => {
    if (shouldSkip()) return;

    const trigger = (reason) => {
      if (shouldSkip()) return;
      window.sessionStorage.setItem(STORAGE_KEY, "1");
      setOpen(true);
      trackEvent("exit_intent_shown", { reason });
    };

    // Trigger #1: timed — guarantees popup shows after 4s on every device
    const timer = setTimeout(() => trigger("timer"), AUTO_OPEN_MS);

    // Trigger #2: exit-intent (desktop) — mouse leaves top edge
    const onMouseLeave = (e) => {
      if (e.clientY <= 0) trigger("exit-intent");
    };
    document.addEventListener("mouseleave", onMouseLeave);

    // Trigger #3: tab visibility — fires when user switches tab / closes window
    const onVisibility = () => {
      if (document.visibilityState === "hidden") trigger("visibility");
    };
    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      clearTimeout(timer);
      document.removeEventListener("mouseleave", onMouseLeave);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [shouldSkip]);

  const close = () => {
    setOpen(false);
    trackEvent("exit_intent_dismissed", {});
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await axios.post(`${API}/leads/magnet`, {
        email,
        language: lang,
        source: "exit-intent",
        magnet: "top-50-grosir-pdf",
      });
      setSuccess(true);
      trackEvent("exit_intent_converted", { email_domain: email.split("@")[1] || "" });
      if (typeof window !== "undefined" && window.fbq) {
        window.fbq("track", "Lead", { source: "exit-intent-magnet" });
      }
    } catch (err) {
      setError(c.errorMsg);
    } finally {
      setLoading(false);
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-ink/70 backdrop-blur-sm animate-fade-in"
      data-testid="exit-intent-popup"
      onClick={close}
    >
      <div
        className="relative w-full max-w-xl bg-white rounded-3xl overflow-hidden soft-shadow animate-fade-up"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={close}
          data-testid="exit-intent-close"
          className="absolute top-4 right-4 z-10 w-9 h-9 rounded-full bg-white/90 hover:bg-white border border-soft flex items-center justify-center text-ink-muted hover:text-ink transition-colors"
          aria-label="close"
        >
          <X className="w-4 h-4" />
        </button>

        {!success ? (
          <div>
            <div className="bg-gradient-to-br from-brand-red via-[#E63E4E] to-brand-orange px-8 pt-10 pb-8 text-white relative overflow-hidden">
              <div className="absolute -top-10 -right-10 w-40 h-40 rounded-full bg-white/10 blur-2xl" />
              <div className="relative">
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/20 backdrop-blur text-[11px] font-bold tracking-wider">
                  {c.badge}
                </div>
                <div className="mt-4 flex items-start gap-4">
                  <div className="w-14 h-14 rounded-2xl bg-white flex items-center justify-center shrink-0">
                    <Gift className="w-7 h-7 text-brand-red" strokeWidth={2.5} />
                  </div>
                  <h2 className="font-display font-extrabold text-2xl sm:text-3xl leading-tight">
                    {c.title}
                  </h2>
                </div>
              </div>
            </div>

            <div className="px-8 py-7">
              <p className="text-ink-muted leading-relaxed">{c.body}</p>

              <div className="mt-5 flex flex-wrap gap-2">
                {[c.feature1, c.feature2, c.feature3].map((f) => (
                  <span key={f} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-accent-subtle text-xs font-semibold text-brand-red">
                    <CheckCircle2 className="w-3.5 h-3.5" />
                    {f}
                  </span>
                ))}
              </div>

              <form onSubmit={submit} className="mt-6 space-y-3" data-testid="exit-intent-form">
                <Input
                  type="email"
                  required
                  placeholder={c.emailLabel}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  data-testid="exit-intent-email"
                  className="h-14 rounded-xl border-soft text-base px-5"
                />
                <Button
                  type="submit"
                  disabled={loading}
                  data-testid="exit-intent-submit"
                  className="w-full h-14 rounded-full bg-brand-red hover:bg-[#A60D25] text-white font-semibold text-base group"
                >
                  <Download className="w-4 h-4 mr-2" />
                  {loading ? c.submitting : c.submit}
                </Button>
                {error && <p className="text-sm text-brand-red text-center" data-testid="exit-intent-error">{error}</p>}
                <button
                  type="button"
                  onClick={close}
                  className="w-full text-center text-xs text-ink-muted hover:text-ink underline underline-offset-2"
                  data-testid="exit-intent-nothanks"
                >
                  {c.noThanks}
                </button>
              </form>
            </div>
          </div>
        ) : (
          <div className="px-8 py-12 text-center" data-testid="exit-intent-success">
            <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-5">
              <CheckCircle2 className="w-9 h-9 text-green-600" />
            </div>
            <h2 className="font-display font-extrabold text-2xl sm:text-3xl text-ink">{c.successTitle}</h2>
            <p className="mt-4 text-ink-muted leading-relaxed max-w-md mx-auto">{c.successBody}</p>
            <a
              href={REGISTER_URL}
              rel="noopener"
              onClick={() => {
                trackEvent("exit_intent_register_click", {});
                if (typeof window !== "undefined" && window.fbq) window.fbq("track", "Lead", { source: "exit-intent-success" });
              }}
              className="inline-block mt-7"
            >
              <Button
                data-testid="exit-intent-register"
                className="rounded-full bg-brand-red hover:bg-[#A60D25] text-white font-semibold h-12 px-7"
              >
                {c.successCta}
              </Button>
            </a>
          </div>
        )}
      </div>
    </div>
  );
}
